import React, { useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { Box } from '@chakra-ui/react';
import Spinner from '../Spinner';
import { useIsOnTop, useModalContext } from './ModalHooks';
import { Bus, Events } from 'core/infrastructure';

const SHOW_DELAY_IN_MS = 800;

type PendingResolve = () => void;

// WaitingOverlay tiene un doble rol: bloquear la pantalla actual durante una operación y hacer de backdrop
// cuando esta pantalla deja de estar en primer plano porque se abre otro dialogo encima.
// Esto se representa con 4 estados diferenciados:
// - hidden: no hay operación activa y la pantalla esta en primer plano; no se renderiza overlay.
// - blocking-hidden: hay operación activa en primer plano; bloquea eventos con opacity 0.01 mientras espera SHOW_DELAY_IN_MS.
// - blocking-visible: la operación sigue activa tras 800ms; se muestra el overlay completo con spinner.
// - backdrop: la pantalla no esta en primer plano; el overlay cubre esta pantalla como fondo del siguiente dialogo, sin spinner.
type OverlayState =
	| { operationActive: false, visualState: 'hidden' }
	| { operationActive: true, visualState: 'blocking-hidden' }
	| { operationActive: true, visualState: 'blocking-visible' }
	| { operationActive: boolean, visualState: 'backdrop' };

type OverlayVisualState = OverlayState['visualState'];

type OverlayView = Readonly<{
	display: 'none' | 'flex',
	opacity: 0 | 0.01 | 1,
	position: 'fixed' | 'absolute',
	showSpinner: boolean,
}>;

const overlayViews = {
	hidden: { display: 'none', opacity: 0, position: 'fixed', showSpinner: false },
	'blocking-hidden': { display: 'flex', opacity: 0.01, position: 'fixed', showSpinner: false },
	'blocking-visible': { display: 'flex', opacity: 1, position: 'fixed', showSpinner: true },
	backdrop: { display: 'flex', opacity: 1, position: 'absolute', showSpinner: false },
} as const satisfies Record<OverlayVisualState, OverlayView>;

function createOverlayState({ operationActive, isOnTop }: { operationActive: boolean; isOnTop: boolean; }): OverlayState {
	if (!isOnTop) {
		return { operationActive, visualState: 'backdrop' };
	}

	return operationActive
		? { operationActive: true, visualState: 'blocking-hidden' }
		: { operationActive: false, visualState: 'hidden' };
}

function resolvePendingPromises(resolves: Set<PendingResolve>) {
	resolves.forEach(resolve => resolve());
	resolves.clear();
}

export interface WaitingOverlayRef {
	show: () => Promise<void>;
	hide: () => void;
}

type OverlayMode = 'on-start' | 'on-first-request';

type OverlayExecuteOptions<T> = {
	waitForPendingRequests?: boolean;
	overlay?: OverlayMode;
	action: () => Promise<T>;
};

export const WaitingOverlay = forwardRef<WaitingOverlayRef, EmptyProps>((_, ref) => {

	const isOnTop = useIsOnTop();

	const [overlayState, setOverlayState] = useState<OverlayState>(() => createOverlayState({ operationActive: false, isOnTop }));

	// Cola de promises a resolver cuando el overlay esté mostrado
	const pendingResolves = useRef<Set<PendingResolve>>(new Set());

	// Hago show awaitable para que se pueda esperar a que se esté mostrando el overlay antes de seguir.
	// Necesito mantener un array de resolves porque se podrían haber ejecutado varios shows a la vez si desde PosScreen se han lanzado
	// varios requests dentro del flujo de un único execute. Los voy a acumulando. Como no existe un setState con callback como antes
	// lo que hago es gestionarlo con un useEffect sobre operationActive, cuando eso sucede resuelvo las promesas.
	useImperativeHandle(ref, () => ({
		show: () => {
			if (overlayState.operationActive)
				return Promise.resolve();

			return new Promise<void>((resolve) => {
				pendingResolves.current.add(resolve);
				setOverlayState(createOverlayState({ operationActive: true, isOnTop }));
			});
		},
		hide: () => {
			setOverlayState(createOverlayState({ operationActive: false, isOnTop }));
		}
	}), [isOnTop, overlayState.operationActive]);

	React.useEffect(() => {
		if (!overlayState.operationActive)
			return;

		resolvePendingPromises(pendingResolves.current);
	}, [overlayState.operationActive]);

	// Uso un LayoutEffect para que se ejecute antes del render y no flashear el overlay
	React.useLayoutEffect(() => {
		setOverlayState(current => {
			const nextState = createOverlayState({ operationActive: current.operationActive, isOnTop });
			return current.visualState === nextState.visualState ? current : nextState;
		});
	}, [isOnTop]);

	React.useEffect(() => {
		if (overlayState.visualState !== 'blocking-hidden')
			return;

		const timeoutId = setTimeout(() => {
			setOverlayState(current => current.visualState === 'blocking-hidden'
				? { operationActive: true, visualState: 'blocking-visible' }
				: current);
		}, SHOW_DELAY_IN_MS);

		return () => clearTimeout(timeoutId);
	}, [overlayState.visualState]);

	// En unmount, resuelvo resolve pendientes para no colgar awaits
	React.useEffect(() => {
		const resolves = pendingResolves.current;
		return () => resolvePendingPromises(resolves);
	}, []);

	const overlayView = overlayViews[overlayState.visualState];

	return <Box
		className='chakra-waiting-overlay'
		display={overlayView.display}
		as='div'
		position={overlayView.position}
		zIndex={'max'}
		inset={0}
		minW='full'
		minH='full'
		bg={{
			base: 'blackAlpha.600',
			_dark: 'blackAlpha.800'
		}}
		opacity={overlayView.opacity}
		tabIndex={0}
		justifyContent='center'
		alignItems='center'
		pointerEvents={'auto'} // Es necesario para que coja los eventos, si no podrían cogerlos las pantallas que están por debajo y podríamos cerrar pantallas sin querer
	>
		{
			// Evito consumir recursos con la animación del spinner si no se está viendo el overlay
			overlayView.showSpinner ? <Spinner /> : null
		}
	</Box>;

});

export const WaitingOverlayContext = React.createContext<Nullable<WaitingOverlayRef>>(null);

export const WaitingOverlayProvider = ({ children }: React.PropsWithChildren) => {

	const overlayRef = useRef<WaitingOverlayRef>(null);

	const show = React.useCallback(async () => {
		return overlayRef.current!.show();
	}, []);

	const hide = React.useCallback(() => {
		// Puede ser que el WaitingOverlay ya esté desmontado desde el dialog:closed. Por eso puede er null
		return overlayRef.current?.hide();
	}, []);

	return (
		<WaitingOverlayContext.Provider value={{ show, hide }} >
			{children}
			<WaitingOverlay ref={overlayRef} />
		</WaitingOverlayContext.Provider>
	);
};

export function useOverlayExecutor() {

	const context = React.useContext(WaitingOverlayContext);
	if (!context) {
		throw new Error('useOverlayExecutor debe usarse dentro de WaitingOverlayProvider');
	}

	const { isOnTop } = useModalContext();

	const showOverlay = React.useCallback(async () => {
		if (isOnTop) {
			await context.show();
		}
	}, [context, isOnTop]);

	const hideOverlay = React.useCallback(() => {
		context.hide();
	}, [context]);

	const execute = React.useCallback(
		async <T,>({
			action,
			overlay = 'on-start',
			waitForPendingRequests = false,
		}: OverlayExecuteOptions<T>): Promise<T> => {
			let unregisterEventHandler: (() => void) | undefined;

			if (overlay === 'on-first-request') {
				unregisterEventHandler = Events.on('request:started', async () => await showOverlay());
			}

			try {
				const shouldWaitForPendingRequests =
					waitForPendingRequests && Bus.getPendingRequests().length > 0;

				if (overlay === 'on-start' || shouldWaitForPendingRequests) {
					await showOverlay();
				}

				if (waitForPendingRequests) {
					await Bus.waitForPendingRequests();
				}

				return await action();
			} finally {
				unregisterEventHandler?.();
				hideOverlay();
			}
		},
		[showOverlay, hideOverlay]
	);

	return {
		execute,
	};
}
